import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";
import { Form } from "capitalrx-components-2.0/lib/forms";

import { getNotificationSettings, updateNotificationSettings } from "@/App/api/notification";
import { ClientAwareLink, ThemedCheckbox, ThemedSubmitButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

interface UnsubscribeForm {
  readonly optIntoEmails: boolean;
  readonly opIntoPushNotifications: boolean;
}

export const UnsubscribePage: FC = () => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const identity = searchParams.get("identity");
  const externalMemberId = searchParams.get("external_member_id");
  const personCode = searchParams.get("person_code");
  const planId = searchParams.get("plan_id");

  const [isLoading, setIsLoading] = useState(true);
  const alert = useAlert();

  const form = useForm<UnsubscribeForm>({
    defaultValues: {
      optIntoEmails: false,
      opIntoPushNotifications: false,
    },
  });

  const navigate = useNavigateClient();

  useEffect(() => {
    if (!identity || !externalMemberId || !personCode || !planId) {
      navigate("/login");
    } else {
      getNotificationSettings({
        identity: identity,
        external_member_id: externalMemberId,
        person_code: personCode,
        plan_id: +planId,
      })
        .then((response) => {
          form.setValue("optIntoEmails", response.opt_in_to_emails);
          form.setValue("opIntoPushNotifications", response.opt_in_to_push_notifications);
          setIsLoading(false);
        })
        .catch(() => {
          navigate("/login");
        });
    }
  }, [form, identity, externalMemberId, personCode, planId]);

  const submitForm = async (formData: UnsubscribeForm) => {
    setIsLoading(true);
    try {
      await updateNotificationSettings({
        opt_in_to_emails: formData.optIntoEmails,
        opt_in_to_push_notifications: formData.opIntoPushNotifications,
        identity: identity,
        external_member_id: externalMemberId,
        person_code: personCode,
        plan_id: +planId,
      });
      alert({
        message: translate("UNSUBSCRIBE_PAGE.notificationPreferencesUpdated"),
        type: "success",
        style: "toast",
      });
      setIsLoading(false);
    } catch (e) {
      alert({
        message: translate("UNSUBSCRIBE_PAGE.failedToUpdateNotificationPreferences"),
        type: "error",
        style: "toast",
      });
      setIsLoading(false);
    }
  };

  return (
    <Stack>
      <Subheading textAlign="center">{translate("UNSUBSCRIBE_PAGE.areYouSureAboutUnsubscribing")}</Subheading>
      <Paragraph textAlign="center">
        {translate("UNSUBSCRIBE_PAGE.unsubscribeAlert")}
      </Paragraph>
      <Form form={form} loading={isLoading} onSubmit={submitForm} testId="unsubscribe">
        <Grid>
          <Grid.Span columns={12}>
            <Stack>
              <Shelf>
                <Shelf.Spacer />
                <ThemedCheckbox.Field testId="opt-into-emails" hideLabel name="optIntoEmails" label={translate("UNSUBSCRIBE_PAGE.allowEmailsToBeSentToYourEmailAddress")} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf>
                <Shelf.Spacer />
                <ThemedCheckbox.Field testId="opt-into-push-notifications" hideLabel name="opIntoPushNotifications" label={translate("UNSUBSCRIBE_PAGE.allowPushNotificationsToBeSentToYou")} />
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          </Grid.Span>

          <Grid.Span columns={12}>
            <ThemedSubmitButton testId="update-preferences">{translate("UNSUBSCRIBE_PAGE.updatePreferences")}</ThemedSubmitButton>
          </Grid.Span>

          <Shelf>
            <Shelf.Spacer />
            <ClientAwareLink testId="to-login" to="/login">{translate("UNSUBSCRIBE_PAGE.backToLogin")}</ClientAwareLink>
            <Shelf.Spacer />
          </Shelf>
        </Grid>
      </Form>
    </Stack>
  );
};
